import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import { Sync as SyncIcon } from '@mui/icons-material';

interface AdminStats {
  lastCrawlTimestamp: string | null;
  documentCount: number;
  isProcessing: boolean;
}

export const SimpleAdminPage: React.FC = () => {
  const [apiKey, setApiKey] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [stats, setStats] = useState<AdminStats>({
    lastCrawlTimestamp: null,
    documentCount: 0,
    isProcessing: false,
  });

  useEffect(() => {
    const storedApiKey = localStorage.getItem('adminApiKey');
    if (storedApiKey) {
      setApiKey(storedApiKey);
      setIsAuthenticated(true);
      fetchStats(storedApiKey);
    }
  }, []);

  const fetchStats = async (key: string) => {
    try {
      const response = await fetch('/api/admin/stats', {
        headers: {
          'X-Admin-API-Key': key,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats({
          lastCrawlTimestamp: data.lastCrawlTimestamp || null,
          documentCount: data.documentCount || 0,
          isProcessing: data.isProcessing || false,
        });
      } else if (response.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem('adminApiKey');
        setError('Invalid API key');
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleAuthenticate = async () => {
    if (!apiKey.trim()) {
      setError('Please enter an API key');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/admin/stats', {
        headers: {
          'X-Admin-API-Key': apiKey,
        },
      });

      if (response.ok) {
        setIsAuthenticated(true);
        localStorage.setItem('adminApiKey', apiKey);
        const data = await response.json();
        setStats({
          lastCrawlTimestamp: data.lastCrawlTimestamp || null,
          documentCount: data.documentCount || 0,
          isProcessing: data.isProcessing || false,
        });
      } else {
        setError('Invalid API key');
      }
    } catch (err) {
      setError('Failed to authenticate');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTriggerCrawl = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch('/api/admin/trigger-crawl', {
        method: 'POST',
        headers: {
          'X-Admin-API-Key': apiKey,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(`Document crawl triggered successfully! Job ID: ${data.jobId}`);
        
        // Refresh stats after a delay
        setTimeout(() => {
          fetchStats(apiKey);
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to trigger crawl');
      }
    } catch (err) {
      setError('Failed to trigger document crawl');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom align="center">
            Admin Access
          </Typography>
          <Typography variant="body1" color="textSecondary" align="center" sx={{ mb: 3 }}>
            Enter your admin API key to continue
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              type="password"
              label="API Key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAuthenticate()}
              disabled={isLoading}
            />
            {error && <Alert severity="error">{error}</Alert>}
            <Button
              fullWidth
              variant="contained"
              onClick={handleAuthenticate}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Authenticate'}
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h3" gutterBottom align="center">
        NVGC Admin Panel
      </Typography>
      
      <Stack spacing={3}>
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}
        
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              System Statistics
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Total Documents
                </Typography>
                <Typography variant="h4">
                  {stats.documentCount.toLocaleString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Last Crawl
                </Typography>
                <Typography variant="body1">
                  {formatTimestamp(stats.lastCrawlTimestamp)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Processing Status
                </Typography>
                <Typography variant="body1" color={stats.isProcessing ? 'primary' : 'textSecondary'}>
                  {stats.isProcessing ? 'Processing...' : 'Idle'}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Document Crawl Control
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Manually trigger a crawl of Nevada Gaming Commission documents. This will scan for new documents and process them.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={isLoading ? <CircularProgress size={20} /> : <SyncIcon />}
              onClick={handleTriggerCrawl}
              disabled={isLoading || stats.isProcessing}
            >
              {isLoading ? 'Triggering Crawl...' : 'Trigger Document Crawl'}
            </Button>
            {stats.isProcessing && (
              <Alert severity="info" sx={{ mt: 2 }}>
                A crawl is already in progress. Please wait for it to complete before starting another.
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => {
                  localStorage.removeItem('adminApiKey');
                  setIsAuthenticated(false);
                  setApiKey('');
                }}
              >
                Logout
              </Button>
              <Button
                variant="outlined"
                onClick={() => fetchStats(apiKey)}
              >
                Refresh Stats
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
};